import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";

const COPY = {
  en: {
    aria: "Exit setup",
    title: "Exit setup?",
    body: "If you exit now, the answers on this screen won't be saved. You can sign back in any time to continue.",
    confirm: "Yes, exit",
    cancel: "Stay",
  },
  hi: {
    aria: "सेटअप से बाहर निकलें",
    title: "सेटअप से बाहर निकलें?",
    body: "अभी बाहर निकलने पर इस स्क्रीन के उत्तर सहेजे नहीं जाएँगे। आप कभी भी फिर साइन इन करके जारी रख सकते हैं।",
    confirm: "हाँ, बाहर",
    cancel: "रुकें",
  },
};

/**
 * Small "X" button shown in onboarding headers. Lets the user abandon the
 * onboarding flow with an explicit confirmation, logging out and returning
 * to the Welcome screen. Without this, hitting the browser back button or
 * killing the tab leaves a stuck onboarding-incomplete account.
 */
export default function OnboardingExit({ className = "" }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { lang } = useLang();
  const c = COPY[lang === "hi" ? "hi" : "en"];
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const confirmExit = async () => {
    setBusy(true);
    try { await logout(); } catch {}
    navigate("/", { replace: true });
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={c.aria}
        data-testid="onboarding-exit-btn"
        className={`inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors ${className}`}
        style={{ border: "1px solid var(--vedari-divider)", color: "var(--vedari-ink-soft)" }}
      >
        <X size={16} strokeWidth={1.6} />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4"
          style={{ backgroundColor: "rgba(0,0,0,0.55)" }}
          onClick={() => !busy && setOpen(false)}
          data-testid="onboarding-exit-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl p-6 animate-gentle-rise"
            style={{ backgroundColor: "var(--vedari-cream)", border: "1px solid var(--vedari-divider)" }}
          >
            <h3 className="font-display text-2xl">{c.title}</h3>
            <p className="text-body mt-3 leading-relaxed font-serif" style={{ color: "var(--vedari-ink-soft)" }}>
              {c.body}
            </p>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setOpen(false)}
                disabled={busy}
                className="vedari-btn-secondary flex-1"
                data-testid="onboarding-exit-cancel-btn"
              >
                {c.cancel}
              </button>
              <button
                onClick={confirmExit}
                disabled={busy}
                className="vedari-btn-primary flex-1"
                data-testid="onboarding-exit-confirm-btn"
              >
                {c.confirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
